import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "";

/** True when running as a static deployment without a backend (Netlify/Vercel-only). */
export const STATIC_MODE = !BACKEND_URL;

export const api = axios.create({
  baseURL: API,
  timeout: 10000,
});

api.interceptors.request.use((config) => {
  if (STATIC_MODE) {
    // Reject early; pages catch and use fallback data
    return Promise.reject(new Error("STATIC_MODE"));
  }
  const token = localStorage.getItem("res_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const SCHOOL = {
  name: "Royal English Medium High School",
  shortName: "Royal English High School",
  tagline: "Royal Education Society (R.)",
  location: "Vijaypura Last Cross, Chikkamagaluru, Karnataka",
  established: 1999,
  whatsapp: "918431352281",
  whatsappDisplay: "+91 84313 52281",
  phone: "919844067816",
  phoneDisplay: "+91 98440 67816",
  email: "Royalschool223@gmail.com",
  socials: {
    youtube: "https://youtube.com/@royalenglishschoolchikmagalur?si=e7I2_aLMXeFOBzfE",
    instagram: "https://instagram.com/royalenglishschoolckm?igshid=YzdkMWQ2MWU=",
    facebook: "https://www.facebook.com/ROYALSCHOOLCHICKMAGLUR",
  },
  mapUrl: "https://maps.app.goo.gl/ATGVZx8oANmbUmzJ6?g_st=aw",
  mapEmbed:
    "https://www.google.com/maps?q=Royal+English+Medium+High+School+Vijaypura+Chikkamagaluru&output=embed",
};

/** Google Drive folders the school manages — open in new tab from the website. */
export const DRIVE_LINKS = {
  admission: "https://drive.google.com/drive/folders/1SPrKwvDWv4Ghe0dkgDjiw4V8X8blhEBo?usp=drive_link",
  gallery: "https://drive.google.com/drive/folders/1bgEY3VFHxsoZN-fTtosO_5-svxMlP0ge?usp=drive_link",
  results: "https://drive.google.com/drive/folders/1zIJdgh5rrBX2RsQNVdH8SKEzfrIcW0lt?usp=drive_link",
};

/** Build a WhatsApp share URL with prefilled message for the school's number. */
export function whatsappShare(message) {
  return `https://wa.me/${SCHOOL.whatsapp}?text=${encodeURIComponent(message)}`;
}

/**
 * Submit form data to Netlify Forms (used when this site is deployed on Netlify).
 * Requires a matching hidden static form in `public/index.html`.
 *
 * @param {string} formName  Must match the `name` attribute on the hidden form.
 * @param {Object} data      Plain object of field name -> value.
 */
export async function submitToNetlify(formName, data) {
  const params = new URLSearchParams();
  params.append("form-name", formName);
  Object.entries(data).forEach(([k, v]) => {
    if (v !== undefined && v !== null) params.append(k, String(v));
  });
  const res = await fetch("/", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params.toString(),
  });
  if (!res.ok) throw new Error(`Netlify form submit failed: ${res.status}`);
}

export const IMAGES = {
  logo: "https://customer-assets.emergentagent.com/job_school-website-build/artifacts/w3thgz4d_royal%20school%20logo.jfif",
  hero: "https://customer-assets.emergentagent.com/job_200a3812-5733-4279-b8fe-7aa1560536a5/artifacts/y1lq5hcz_royal%20english%20main%20theme.jfif",
  building: "https://customer-assets.emergentagent.com/job_200a3812-5733-4279-b8fe-7aa1560536a5/artifacts/pfr62on1_royal%20english%20main%20theme.jfif",
  graduation: "https://customer-assets.emergentagent.com/job_200a3812-5733-4279-b8fe-7aa1560536a5/artifacts/qfmprvv3_image%202.jfif",
  sports: "https://customer-assets.emergentagent.com/job_200a3812-5733-4279-b8fe-7aa1560536a5/artifacts/3lbojijh_sports.jfif",
  group: "https://customer-assets.emergentagent.com/job_200a3812-5733-4279-b8fe-7aa1560536a5/artifacts/6w9da9vx_image%205.jfif",
  chairman: "https://customer-assets.emergentagent.com/job_school-website-build/artifacts/vkbjh8dd_royal%20chairman.jfif",
  principal: "https://customer-assets.emergentagent.com/job_school-website-build/artifacts/k88e65bv_royal%20prinipal.jfif",
  sportsPyramid: "https://customer-assets.emergentagent.com/job_school-website-build/artifacts/sjyra4ps_royal%2022.jfif",
  environmentDay: "https://customer-assets.emergentagent.com/job_school-website-build/artifacts/ozeti0ut_royal%2033.jfif",
};

export const PRINCIPAL_MESSAGE = `Welcome to Royal English Medium High School, Chikmagalur.

Education is the foundation for a bright and successful future. At Royal English Medium High School, we are committed to providing quality education that nurtures knowledge, discipline, confidence, and good values in every student. Our aim is not only academic excellence but also the overall development of each child.

We believe that every student has unique talents and potential. Through dedicated teaching, modern learning methods, and a supportive environment, we encourage students to think creatively, act responsibly, and grow into confident individuals who can contribute positively to society.

Our school focuses on building strong character, leadership qualities, and a lifelong love for learning. Along with academics, we encourage participation in sports, cultural activities, and co-curricular programs to help students develop their skills and personality.

I sincerely thank all parents for their trust and cooperation. Together, let us guide our children toward a successful and meaningful future.

Wishing all our students great success in their educational journey.`;

export const CHAIRMAN_MESSAGE = `Welcome to Royal English Medium High School, Chikmagalur.

It is with great pride and pleasure that I welcome you to our institution's official website. At Royal English Medium High School, we are committed to providing an educational environment that inspires excellence, nurtures character, and empowers students to become confident and responsible individuals.

Education is not merely the acquisition of knowledge; it is the foundation for building values, leadership, discipline, and lifelong learning. Our institution strives to create a balanced learning atmosphere where academic achievement is complemented by moral development, creativity, and social responsibility.

We strongly believe that every child possesses unique potential. Through dedicated faculty, innovative teaching practices, and a student-centered approach, we encourage our learners to explore their talents and develop the skills necessary to meet the challenges of a rapidly changing world.

Our vision is to prepare students not only for examinations, but for life itself — equipping them with confidence, integrity, compassion, and a spirit of excellence. We continuously work towards maintaining high educational standards while ensuring a safe, inclusive, and motivating environment for every student.

I express my sincere gratitude to our parents, teachers, and well-wishers for their unwavering trust and support. Together, we will continue to shape a brighter future for our children and society.

I warmly invite you to be a part of the Royal English Medium High School family and join us in our journey toward educational excellence.`;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
